import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  BrandsEnum,
  DeviceService,
  ICardStore,
  IconSizeEnum,
  PositionEnum, SdButtonComponent, SdButtonIconComponent, SdHtmlComponent, SdIconComponent,
  SdIsCollapsibleDirective, SdLinkComponent, SdListComponent, SdTitleComponent,
  StoreTypeEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet, NgClass, LowerCasePipe } from '@angular/common';
import { RelayPointRecapComponent } from '@seb/web-common/cart/components/relay-point-recap/relay-point-recap.component';

@Component({
  selector: 'sd-card-store',
  templateUrl: './card-store.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./card-store.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdIsCollapsibleDirective,
    SdTitleComponent,
    SdIconComponent,
    SdButtonIconComponent,
    SdListComponent,
    NgTemplateOutlet,
    SdButtonComponent,
    NgClass,
    SdLinkComponent,
    LowerCasePipe,
    TranslateModule,
    SdHtmlComponent,
    RelayPointRecapComponent
  ]
})
export class SdCardStoreComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private deviceService: DeviceService,
    public elRef: ElementRef,
  ) {}

  storeType = StoreTypeEnum;
  iconSize = IconSizeEnum;
  positionEnum = PositionEnum;
  selected = false;
  isMobile$: boolean;
  mobileSubscription: Subscription;
  idCard: string = uuid();
  themesEnum = ThemesEnum;

  @Input()
  brand: BrandsEnum;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 4;

  /**
   * Allow to add a specific color to the title (used to have the same color of the filter and map pin)
   * If there no color set, it will be depending of the store's type (type === 'store' => brand color else default color)
   */
  @Input()
  color: string;

  @Input()
  storeInfo: ICardStore;

  @Input()
  checkboxLabel: string;

  @ViewChild('actionPickup', { read: ElementRef })
  actionPickup: ElementRef;

  @ViewChild('actionNonPickup', { read: ElementRef })
  actionNonPickup: ElementRef;

  @ViewChild('hidePickup', { read: ElementRef })
  hidePickup: ElementRef;

  @ViewChild('hideNonPickup', { read: ElementRef })
  hideNonPickup: ElementRef;

  /**
   * Id for the current card
   */
  @Input()
  idCardCollapse = 'idCardCollapse';

  @Input()
  isCardOpened = false;

  /**
   * URL for the generated itinerary
   */
  @Input()
  itinaryUrl: string;

  /**
   * Either the card is on 'pickup' mode (can be selected) or not
   */
  @Input()
  pickup = false;

  /**
   * Either the card is a 'repair center' or not
   */
  @Input()
  repairCenter = false;

  @Input()
  hideActions = false;

  @Input()
  hasProduct = false;

  @Input()
  hasCloseButton = false;

  /**
   * Defined the readOnly mode
   */
  @Input()
  readOnly = false;

  @Input()
  selectedPickupId: string;

  @Output()
  storeClicked = new EventEmitter<void>();

  @Output()
  storeChoosed = new EventEmitter<void>();

  @Input()
  buttonText: string;

  @Output()
  closeCard = new EventEmitter<void>();

  @Output()
  repairCenterSelected = new EventEmitter<void>();

  @Output()
  itenaryClicked = new EventEmitter<void>();

  @Output()
  learnMoreClicked = new EventEmitter<void>();

  @ViewChild('titleEl', { read: ElementRef })
  titleEl: ElementRef;

  @Input()
  class = ''; // override the standard class attr with a new one.

  @Input()
  isHob = false;

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.pickup ? 'is-pickup' : '',
      this.repairCenter ? 'is-repair-center' : '',
      this.readOnly ? 'is-readonly' : '',
      this.selected ? 'is-selected' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit() {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((x) => {
      this.isMobile$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.mobileSubscription?.unsubscribe();
  }

  onClose(): void {
    this.closeCard.emit();
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
    if (this.color) {
      // set the card title color
      this.renderer.setStyle(this.titleEl.nativeElement, 'color', this.color);
    }
  }

  selectRepairCenter() {
    this.repairCenterSelected.emit();
  }

  itenaryClick() {
    this.itenaryClicked.emit();
  }

  learnMoreClick() {
    this.learnMoreClicked.emit();
  }

  toggle() {
    this.selected = !this.selected;
    if (!this.pickup) {
      this.isCardOpened = !this.isCardOpened;
    }
    this.storeClicked.emit();
  }

  chooseStore() {
    this.storeChoosed.emit();
  }

  get actionElement(): HTMLElement | undefined {
    return this.pickup
      ? this.actionPickup?.nativeElement
      : this.actionNonPickup?.nativeElement;
  }

  get collapsibleElement(): HTMLElement | undefined {
    return this.pickup
      ? this.hidePickup?.nativeElement
      : this.hideNonPickup?.nativeElement;
  }

  triggerClick() {
    if (this.pickup) {
      this.toggle();
    } else {
      const actionButton = this.actionNonPickup?.nativeElement;
      if (actionButton) {
        actionButton.click();
      } else {
        this.toggle();
      }
    }
  }
}
